/**
 * Renderer del dashboard publicado para el tenant.
 *
 * Carga GET /api/dashboards/:id, pinta tabs + filtros + widgets.
 * Para cada widget pide POST /api/dashboards/:id/widgets/:wid/data.
 * Renderiza KPI / tabla / bar / line / pie según widget.type.
 *
 * Uso: <DashboardRenderer dashboardId="uuid" />
 * La ruta /dashboard/:id en App.tsx (o App-level) monta este componente.
 *
 * Nota: el usuario debe ser miembro del tenant owner del dashboard (gate server).
 */
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import {
  DashboardApiError,
  type DashboardFilter,
  type DashboardTab,
  type DashboardWidget,
  type TenantDashboardDetail,
  type WidgetDataResponse,
  fetchTenantDashboard,
  fetchTenantWidgetData,
} from "../../lib/dashboard-builder-api";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
);

// ---------------------------------------------------------------------------
// Estilos (token del tema oscuro del dashboard)
// ---------------------------------------------------------------------------

const GRID = "#30363d";
const TICK = "#7c8290";
const COLORS = ["#4f9cf9", "#34d399", "#a78bfa", "#fbbf24", "#fb923c", "#f87171"];

// ---------------------------------------------------------------------------
// Sub-componentes de widget
// ---------------------------------------------------------------------------

function KpiWidget({ data, widget }: { data: WidgetDataResponse; widget: DashboardWidget }) {
  const value = data.rows[0]?.[0] ?? "—";
  const sub = data.rows[0]?.[1] ?? null;
  const config = widget.config ?? {};
  const colorKey = typeof config.color === "string" ? config.color : undefined;
  const color = colorKey ?? "#4f9cf9";

  return (
    <div
      style={{ display: "flex", flexDirection: "column", justifyContent: "center", height: "100%" }}
    >
      <div style={{ fontSize: "28px", fontWeight: 700, color, lineHeight: 1.1 }}>
        {String(value)}
      </div>
      {sub !== null && (
        <div style={{ fontSize: "11px", color: TICK, marginTop: "4px" }}>{String(sub)}</div>
      )}
    </div>
  );
}

function TableWidget({ data }: { data: WidgetDataResponse }) {
  return (
    <div style={{ overflowX: "auto", flex: 1 }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "11px",
        }}
      >
        <thead>
          <tr>
            {data.columns.map((col) => (
              <th
                key={col}
                style={{
                  borderBottom: `1px solid ${GRID}`,
                  padding: "3px 6px",
                  textAlign: "left",
                  color: TICK,
                  fontWeight: 600,
                }}
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, ri) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: stable index for static data rows
            <tr key={ri}>
              {(row as unknown[]).map((cell, ci) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: stable index for cell values
                <td key={ci} style={{ borderBottom: `1px solid ${GRID}`, padding: "3px 6px" }}>
                  {String(cell ?? "")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BarWidget({ data, widget }: { data: WidgetDataResponse; widget: DashboardWidget }) {
  const config = widget.config ?? {};
  const colX = typeof config.columnX === "string" ? config.columnX : (data.columns[0] ?? "");
  const colY = typeof config.columnY === "string" ? config.columnY : (data.columns[1] ?? "");
  const xi = data.columns.indexOf(colX);
  const yi = data.columns.indexOf(colY);
  const labels = data.rows.map((r) => String((r as unknown[])[xi] ?? ""));
  const values = data.rows.map((r) => {
    const v = (r as unknown[])[yi];
    return typeof v === "number" ? v : Number(v);
  });

  const chartData = {
    labels,
    datasets: [{ label: colY, data: values, backgroundColor: "#4f9cf9" }],
  };
  const opts = {
    plugins: { legend: { display: false } },
    maintainAspectRatio: false,
    scales: {
      x: { ticks: { color: TICK, font: { size: 9 } }, grid: { display: false } },
      y: { ticks: { color: TICK, font: { size: 9 } }, grid: { color: GRID } },
    },
  } as const;

  return <Bar data={chartData} options={opts} />;
}

function LineWidget({ data, widget }: { data: WidgetDataResponse; widget: DashboardWidget }) {
  const config = widget.config ?? {};
  const colX = typeof config.columnX === "string" ? config.columnX : (data.columns[0] ?? "");
  const colY = typeof config.columnY === "string" ? config.columnY : (data.columns[1] ?? "");
  const xi = data.columns.indexOf(colX);
  const yi = data.columns.indexOf(colY);
  const labels = data.rows.map((r) => String((r as unknown[])[xi] ?? ""));
  const values = data.rows.map((r) => {
    const v = (r as unknown[])[yi];
    return typeof v === "number" ? v : Number(v);
  });

  const chartData = {
    labels,
    datasets: [
      {
        label: colY,
        data: values,
        borderColor: "#4f9cf9",
        backgroundColor: "rgba(79,156,249,0.1)",
        tension: 0.3,
        fill: true,
      },
    ],
  };
  const opts = {
    plugins: { legend: { display: false } },
    maintainAspectRatio: false,
    scales: {
      x: { ticks: { color: TICK, font: { size: 9 } }, grid: { display: false } },
      y: { ticks: { color: TICK, font: { size: 9 } }, grid: { color: GRID } },
    },
  } as const;

  return <Line data={chartData} options={opts} />;
}

function PieWidget({ data, widget }: { data: WidgetDataResponse; widget: DashboardWidget }) {
  const config = widget.config ?? {};
  const colLabel = typeof config.columnX === "string" ? config.columnX : (data.columns[0] ?? "");
  const colVal = typeof config.columnY === "string" ? config.columnY : (data.columns[1] ?? "");
  const li = data.columns.indexOf(colLabel);
  const vi = data.columns.indexOf(colVal);
  const labels = data.rows.map((r) => String((r as unknown[])[li] ?? ""));
  const values = data.rows.map((r) => {
    const v = (r as unknown[])[vi];
    return typeof v === "number" ? v : Number(v);
  });

  const chartData = {
    labels,
    datasets: [{ data: values, backgroundColor: COLORS }],
  };
  const opts = {
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: { color: TICK, font: { size: 10 }, boxWidth: 10 },
      },
    },
    maintainAspectRatio: false,
  };

  return <Doughnut data={chartData} options={opts} />;
}

function WidgetContainer({
  widget,
  dashboardId,
  filters,
}: {
  widget: DashboardWidget;
  dashboardId: string;
  filters: Record<string, string>;
}) {
  const [data, setData] = useState<WidgetDataResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Serializar filtros para usar como dependency estable del effect
  // (el objeto `filters` cambia de referencia en cada render del padre).
  const filterKey = useMemo(() => JSON.stringify(filters), [filters]);

  useEffect(() => {
    if (!widget.datasourceId) {
      setLoading(false);
      return;
    }
    const currentFilters: Record<string, string> = JSON.parse(filterKey) as Record<string, string>;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchTenantWidgetData(dashboardId, widget.id, currentFilters)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof DashboardApiError ? err.message : "Error al cargar datos");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dashboardId, widget.id, widget.datasourceId, filterKey]);

  return (
    <div
      style={{
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
        borderRadius: "11px",
        padding: "12px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        minHeight: "80px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "8px",
        }}
      >
        <span style={{ fontSize: "12.5px", fontWeight: 600 }}>{widget.title}</span>
        {loading && <span style={{ fontSize: "10px", color: TICK }}>cargando…</span>}
      </div>

      {error && <div style={{ fontSize: "11px", color: "#f87171", padding: "8px 0" }}>{error}</div>}

      {!widget.datasourceId && !loading && (
        <div style={{ fontSize: "11px", color: TICK }}>Sin datasource</div>
      )}

      {data && !loading && (
        <div style={{ flex: 1, overflow: "hidden" }}>
          {widget.type === "kpi" && <KpiWidget data={data} widget={widget} />}
          {widget.type === "table" && <TableWidget data={data} />}
          {widget.type === "bar" && (
            <div style={{ height: "100%", minHeight: "120px" }}>
              <BarWidget data={data} widget={widget} />
            </div>
          )}
          {widget.type === "line" && (
            <div style={{ height: "100%", minHeight: "120px" }}>
              <LineWidget data={data} widget={widget} />
            </div>
          )}
          {widget.type === "pie" && (
            <div style={{ height: "100%", minHeight: "120px" }}>
              <PieWidget data={data} widget={widget} />
            </div>
          )}
          {widget.type === "text" && (
            <div style={{ fontSize: "12px", color: "var(--color-text)", lineHeight: 1.6 }}>
              {data.message ?? "Widget de texto"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente de filtros
// ---------------------------------------------------------------------------

function FilterBar({
  filters,
  values,
  onChange,
}: {
  filters: DashboardFilter[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
}) {
  if (filters.length === 0) return null;

  return (
    <div
      style={{
        display: "flex",
        gap: "12px",
        flexWrap: "wrap",
        padding: "10px 16px",
        borderBottom: "1px solid var(--color-border)",
        background: "var(--color-surface)",
        alignItems: "center",
      }}
    >
      {filters.map((f) => {
        const isGlobal = f.tabId === null;
        const borderColor = isGlobal ? "#a78bfa" : "#fbbf24";
        return (
          <div
            key={f.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "12px",
              background: "var(--color-surface-2)",
              border: `1px solid ${borderColor}`,
              borderRadius: "20px",
              padding: "4px 10px",
            }}
          >
            <span
              style={{
                width: "7px",
                height: "7px",
                borderRadius: "50%",
                background: borderColor,
                flexShrink: 0,
              }}
            />
            <label
              htmlFor={`filter-${f.id}`}
              style={{ color: TICK, fontSize: "11px", cursor: "pointer" }}
            >
              {f.label}
            </label>
            <input
              id={`filter-${f.id}`}
              type="text"
              value={values[f.key] ?? f.defaultValue ?? ""}
              onChange={(e) => onChange(f.key, e.target.value)}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--color-text)",
                fontSize: "12px",
                outline: "none",
                width: "80px",
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Renderer principal
// ---------------------------------------------------------------------------

interface DashboardRendererProps {
  dashboardId: string;
}

export function DashboardRenderer({ dashboardId }: DashboardRendererProps) {
  const [detail, setDetail] = useState<TenantDashboardDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchTenantDashboard(dashboardId)
      .then((data) => {
        setDetail(data);
        if (data.tabs.length > 0) {
          setActiveTabId(data.tabs[0]?.id ?? null);
        }
        // Inicializar filtros con defaultValues
        const defaults: Record<string, string> = {};
        for (const f of data.filters) {
          if (f.defaultValue) defaults[f.key] = f.defaultValue;
        }
        setFilterValues(defaults);
      })
      .catch((err) => {
        setError(err instanceof DashboardApiError ? err.message : "Error al cargar el dashboard");
      })
      .finally(() => setLoading(false));
  }, [dashboardId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFilterChange = useCallback((key: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "64px" }}>
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: "32px 24px",
          background: "rgba(248,113,113,0.08)",
          border: "1px solid rgba(248,113,113,0.35)",
          borderRadius: "10px",
          color: "#f87171",
          fontSize: "13px",
          margin: "24px",
        }}
      >
        {error}
        <button
          type="button"
          onClick={load}
          style={{
            marginLeft: "12px",
            padding: "4px 10px",
            background: "var(--color-surface-2)",
            border: "1px solid var(--color-border)",
            borderRadius: "6px",
            color: "var(--color-text)",
            fontSize: "11px",
            cursor: "pointer",
          }}
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (!detail) return null;

  const { dashboard, tabs, widgets, filters } = detail;

  const activeTab: DashboardTab | undefined = tabs.find((t) => t.id === activeTabId);
  const globalFilters: DashboardFilter[] = filters.filter((f) => f.tabId === null);
  const tabFilters: DashboardFilter[] = filters.filter((f) => f.tabId === activeTabId);
  const visibleFilters: DashboardFilter[] = [...globalFilters, ...tabFilters];

  const widgetsInTab: DashboardWidget[] = widgets.filter((w) => w.tabId === activeTabId);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        background: "#0d1117",
        minHeight: "100%",
        color: "var(--color-text)",
      }}
    >
      {/* Header del dashboard */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "12px 16px",
          background: "#11161f",
          borderBottom: "1px solid var(--color-border)",
        }}
      >
        <h1 style={{ margin: 0, fontSize: "15px", fontWeight: 600 }}>{dashboard.name}</h1>
        <span
          style={{
            fontSize: "10.5px",
            padding: "1px 8px",
            borderRadius: "10px",
            border: "1px solid var(--color-border)",
            background: "var(--color-surface-2)",
            color: TICK,
          }}
        >
          publicado
        </span>
      </div>

      {/* Tabs */}
      {tabs.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: "2px",
            padding: "0 16px",
            background: "#11161f",
            borderBottom: "1px solid var(--color-border)",
          }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTabId(tab.id)}
              style={{
                padding: "8px 16px",
                fontSize: "12.5px",
                fontWeight: 500,
                color: tab.id === activeTabId ? "#4f9cf9" : TICK,
                background: "transparent",
                border: "none",
                borderBottom: `2px solid ${tab.id === activeTabId ? "#4f9cf9" : "transparent"}`,
                cursor: "pointer",
                transition: "color 0.1s, border-color 0.1s",
              }}
              aria-selected={tab.id === activeTabId}
            >
              {tab.name}
            </button>
          ))}
        </div>
      )}

      {/* Filtros */}
      <FilterBar filters={visibleFilters} values={filterValues} onChange={handleFilterChange} />

      {/* Lienzo de widgets */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px" }}>
        {!activeTab && <div style={{ color: TICK, fontSize: "13px" }}>Sin tabs disponibles.</div>}

        {activeTab && widgetsInTab.length === 0 && (
          <div style={{ color: TICK, fontSize: "13px" }}>Este tab no tiene widgets.</div>
        )}

        {widgetsInTab.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(12, 1fr)",
              gridAutoRows: "118px",
              gap: "12px",
            }}
          >
            {widgetsInTab.map((w) => (
              <div
                key={w.id}
                style={{
                  gridColumn: `span ${Math.min(w.layoutW, 12)}`,
                  gridRow: `span ${Math.max(w.layoutH, 1)}`,
                }}
              >
                <WidgetContainer widget={w} dashboardId={dashboard.id} filters={filterValues} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
